import React from "react";
import PageLayout from "../components/PageLayout";
import PageHero from "../components/PageHero";
import ContactCTA from "../components/ContactCTA";
import { specialServices } from "../data/mock";
import {
  Scissors,
  Palette,
  GitBranch,
  Layers,
  Boxes,
  PenTool,
  ArrowRight,
  Workflow,
} from "lucide-react";
import { Link } from "react-router-dom";

const iconMap = { Scissors, Palette, GitBranch, Layers, Boxes, PenTool };

const processSteps = [
  { num: "01", title: "Consult", description: "Tell us your spec, environment and timeline. We listen first." },
  { num: "02", title: "Design", description: "Our engineers translate requirements into a buildable design and BOM." },
  { num: "03", title: "Build", description: "Material is cut, striped, twisted, jacketed and kitted in-house." },
  { num: "04", title: "Deliver", description: "Labeled, packaged and delivered ready for installation — on time." },
];

const SpecialServices = () => {
  return (
    <PageLayout>
      <PageHero
        badge="Value-Added Services"
        title="Special Services & Custom Engineering"
        subtitle="Receive material exactly the way you need it — cut, striped, twisted, jacketed, kitted and ready for the field."
        breadcrumbs={[{ label: "Special Services" }]}
      />

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-4 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
              Our Capabilities
            </span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" style={{ color: "#0a2e1f" }}>
              Beyond stock product
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {specialServices.map((s, idx) => {
              const Icon = iconMap[s.icon] || Workflow;
              return (
                <div
                  key={idx}
                  className="group relative overflow-hidden rounded-3xl p-8 bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/10 hover:-translate-y-1 transition-all duration-500"
                >
                  <div
                    className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"
                    style={{ background: "radial-gradient(circle, #10b981, transparent 70%)" }}
                  />
                  <div className="relative">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-emerald-500/30"
                      style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
                    >
                      <Icon className="text-white" size={24} />
                    </div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: "#0a2e1f" }}>
                      {s.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{s.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20" style={{ backgroundColor: "#f7faf8" }}>
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-4 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
              How We Work
            </span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight" style={{ color: "#0a2e1f" }}>
              A simple, transparent process
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, idx) => (
              <div
                key={idx}
                className="relative rounded-3xl p-8 bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-500"
              >
                <div
                  className="text-5xl font-extrabold mb-4 bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      "linear-gradient(135deg, #10b981 0%, #047857 100%)",
                  }}
                >
                  {step.num}
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: "#0a2e1f" }}>{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inline CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-[1100px] mx-auto px-6">
          <div
            className="rounded-3xl p-10 md:p-14 grid md:grid-cols-[1fr_auto] items-center gap-8"
            style={{ background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)" }}
          >
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-2 tracking-tight" style={{ color: "#0a2e1f" }}>
                Have a custom build in mind?
              </h3>
              <p className="text-slate-700 leading-relaxed">
                Send us your drawings, specs or rough idea — our engineering team
                will scope it, price it and prototype it.
              </p>
            </div>
            <Link
              to="/request-info"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white text-sm font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-[1.03] transition-all whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
            >
              Request Info <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <ContactCTA />
    </PageLayout>
  );
};

export default SpecialServices;
